/**
 * 평수 표기 — 한 곳에서만 계산한다 (2026-09-26 대표: "84A, 123A 로만 쓰니 몇 평인지 모르겠다").
 *
 * 흔히 말하는 "34평"은 공급면적 기준이다(전용 84㎡ ≈ 공급 112㎡ ≈ 34평).
 *   - 공급면적이 있으면 공급 기준 평수 → "35평"
 *   - 공급면적이 없고 전용만 있으면 기준을 밝혀 → "전용 25평" (공급 평수와 헷갈리지 않게)
 * 1평 = 3.3058㎡ (서버 평당가 계산과 같은 값)
 */
import type { Property, PropertyTypePrice } from '../../services/api';
import { normalizeTypeName } from '../../utils/typeName';

export const M2_PER_PYEONG = 3.3058;

const isPos = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

export const toPyeong = (m2: number): number => Math.round(m2 / M2_PER_PYEONG);

type AreaRow = Pick<PropertyTypePrice, 'supply_area' | 'exclusive_area'>;

/** 한 평형의 평수 라벨 — "35평" / "전용 25평" / null */
export function typePyeongLabel(row: AreaRow | null | undefined): string | null {
  if (!row) return null;
  if (isPos(row.supply_area)) return `${toPyeong(row.supply_area)}평`;
  if (isPos(row.exclusive_area)) return `전용 ${toPyeong(row.exclusive_area)}평`;
  return null;
}

const norm = (s: string) => normalizeTypeName(s).replace(/\s+/g, '').toUpperCase();

/** 타입명(84A)으로 그 현장의 평형 행을 찾는다 — 정확히 같거나 접두어로 맞는 행 */
export function findTypeRow(property: Pick<Property, 'type_prices'>, typeName: string): PropertyTypePrice | null {
  const rows = property.type_prices;
  const t = norm(typeName || '');
  if (!Array.isArray(rows) || !t) return null;
  return (
    rows.find((r) => norm(r.type_name) === t) ||
    rows.find((r) => norm(r.type_name).startsWith(t) || t.startsWith(norm(r.type_name))) ||
    null
  );
}

/** 타입명으로 평수 라벨 — 평면도 칩 등 */
export function pyeongForType(property: Pick<Property, 'type_prices'>, typeName: string): string | null {
  return typePyeongLabel(findTypeRow(property, typeName));
}

/**
 * 현장 전체 평수 범위 — "25~35평" / "35평" / "전용 18~25평" / null.
 * 평형 행의 공급면적을 우선 쓰고, 없으면 min_area·max_area(전용)로 기준을 밝힌다.
 */
export function propertyPyeongRange(
  property: Pick<Property, 'type_prices' | 'min_area' | 'max_area'> & Partial<Pick<Property, 'supply_area_min' | 'supply_area_max'>>,
): string | null {
  // 상세 API 는 type_prices, 지도·목록 API 는 supply_area_min/max 로 공급면적을 준다
  const fromRows = (property.type_prices || []).map((r) => r.supply_area);
  const sup = (fromRows.some(isPos) ? fromRows : [property.supply_area_min, property.supply_area_max])
    .filter(isPos)
    .map(toPyeong);
  if (sup.length) {
    const lo = Math.min(...sup);
    const hi = Math.max(...sup);
    return lo === hi ? `${lo}평` : `${lo}~${hi}평`;
  }
  const ex = [property.min_area, property.max_area].filter(isPos).map(toPyeong);
  if (ex.length) {
    const lo = Math.min(...ex);
    const hi = Math.max(...ex);
    return lo === hi ? `전용 ${lo}평` : `전용 ${lo}~${hi}평`;
  }
  return null;
}
